/**
 * Based on https://github.com/vercel/next.js/tree/canary/examples/with-google-tag-manager
 */

import Script from 'next/script'
import { getCookie } from 'cookies-next'
import { logger } from '@/services/logger/logger.client'
import type { CountryCode } from '@/utils/l10n/types'
import { type TrackingEvent } from './Tracking/TrackingEvent'

const GTM_ID = process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID
const GTM_ENVIRONMENT = process.env.NEXT_PUBLIC_GTM_ENV
// Set to the measurement path proxied by gtmGatewayMiddleware (e.g. /metrics) to serve gtm.js first-party
const GTM_GATEWAY_PATH = process.env.NEXT_PUBLIC_GTM_GATEWAY_PATH
const GTM_SCRIPT_URL = GTM_GATEWAY_PATH
  ? `${GTM_GATEWAY_PATH}/gtm.js?id=`
  : 'https://www.googletagmanager.com/gtm.js?id='

const GTM_ENVIRONMENTS = ['local', 'staging', 'prod'] as const
type GTMEnvironment = (typeof GTM_ENVIRONMENTS)[number]

const getGtmEnvironment = () => {
  if (isGTMEnvironment(GTM_ENVIRONMENT)) return GTM_ENVIRONMENT

  const environments = GTM_ENVIRONMENTS.join('|')
  const message = `Unknown GTM environment ${GTM_ENVIRONMENT}, expected <${environments}>`
  if (GTM_ID) {
    throw new Error(message)
  } else {
    logger.warn(message)
  }
}

const isGTMEnvironment = (value: unknown): value is GTMEnvironment => {
  return GTM_ENVIRONMENTS.includes(value as GTMEnvironment)
}

type GTMUserProperties = {
  siteVersion: string
  country: CountryCode
  environment?: GTMEnvironment
}

type GTMPageData = {
  page: string
  title: string
}

export type GTMEcommerceData = Record<string, unknown>

export type GTMShopSessionData = {
  id: string | undefined
}

type DataLayerObject = {
  event?: string
  userProperties?: GTMUserProperties
  offerData?: Record<string, unknown>
  eventData?: Record<string, unknown>
  OnetrustActiveGroups?: string
  pageData?: GTMPageData
  ecommerce?: GTMEcommerceData
  shopSession?: GTMShopSessionData
  user_id?: string
}

type DataLayerArray = Array<unknown>

export type EcommerceEvent = {
  event: TrackingEvent
  ecommerce: GTMEcommerceData
  shopSession: GTMShopSessionData
  // https://support.google.com/google-ads/answer/9917012?hl=en
  new_customer?: boolean
  price_match?: {
    exposure_matched: boolean
    price_matched: boolean
  }
}

// Needed in case event is sent before GTM is loaded, see https://github.com/HedvigInsurance/racoon/commit/38dbb73d552a590f652bbbe537d4d8ed4b0399f8
export const pushToGTMDataLayer = (obj: DataLayerObject | DataLayerArray) => {
  if (!window.dataLayer) window.dataLayer = []
  // Clear the previous ecommerce object
  if (!Array.isArray(obj) && typeof obj.ecommerce !== 'undefined') {
    window.dataLayer.push({ ecommerce: null })
  }
  window.dataLayer.push(obj)
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export function gtmGtag(..._args: Array<any>) {
  if (!window.dataLayer) window.dataLayer = []
  // We need to forward arguments directly for it to work
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments)
}

export const GTMAppScript = () => {
  if (!GTM_ID) return null

  return (
    <Script
      id="gtag-base"
      strategy="lazyOnload"
      dangerouslySetInnerHTML={{
        __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'${GTM_SCRIPT_URL}'+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer', '${GTM_ID}');`,
      }}
    />
  )
}

export const initializeGtm = (countryCode: CountryCode) => {
  pushToGTMDataLayer({
    event: 'initiate_gtm',
    userProperties: {
      siteVersion: 'racoon',
      country: countryCode,
      environment: getGtmEnvironment(),
    },
  })

  // Get consent categories from OneTrust cookie
  const oneTrustConsentCookie = getCookie('OptanonConsent') as string | undefined

  // Check for accepted categories, C0001 is always required
  const activeGroups = ['C0001']
  for (const group of ['C0002', 'C0003', 'C0004', 'C0005']) {
    if (oneTrustConsentCookie?.includes(`${group}:1`)) {
      activeGroups.push(group)
    }
  }

  // Update OneTrust active groups for necessary category on app init
  // The format should be a comma seperated string: ",C0001,C0002,C0003,C0004,C0005,"
  pushToGTMDataLayer({
    event: 'OneTrustGroupsUpdated',
    OnetrustActiveGroups: `,${activeGroups.join(',')},`,
  })

  // Map consent categories to GTM consent mode
  const consentGroups = {
    required: 'granted',
    performance: activeGroups.includes('C0002') ? 'granted' : 'denied',
    functional: activeGroups.includes('C0003') ? 'granted' : 'denied',
    targeting: activeGroups.includes('C0004') ? 'granted' : 'denied',
    social: activeGroups.includes('C0005') ? 'granted' : 'denied',
  }

  // Update GTM consent mode
  gtmGtag('consent', 'update', {
    ad_storage: consentGroups.targeting,
    ad_user_data: consentGroups.targeting,
    ad_personalization: consentGroups.targeting,
    analytics_storage: consentGroups.performance,
    personalization_storage: consentGroups.required,
    functionality_storage: consentGroups.required,
    security_storage: consentGroups.required,
  })
}

// https://developers.google.com/analytics/devguides/collection/ga4/user-id?client_type=gtm#send_user_ids
export const setUserId = (userId: string) => {
  pushToGTMDataLayer({ user_id: userId })
}
